import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
} from "@mui/material";
import { Add, Delete, Edit, MeetingRoom, Receipt, NoteAlt } from "@mui/icons-material";
import HotelIcon from "@mui/icons-material/Hotel";
import ChairIcon from "@mui/icons-material/Chair";
import toast from "react-hot-toast";
import PendingBookingsModal from "./PendingBookingsModal";
import BookingModal from "./BookingModal";
import BedSeatNotesModal from "./BedSeatNotesModal";

const API = process.env.REACT_APP_API_URL;

const RoomLibraryManager = ({ viewMode = "room" }) => {
  const [data, setData] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [openRoomModal, setOpenRoomModal] = useState(false);
  const [editRoom, setEditRoom] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
  });
  const [form, setForm] = useState({
    number: "",
    description: "",
    room_id: "",
  });
  const [roomForm, setRoomForm] = useState({
    room_number: "",
    capacity: "",
  });
  const [selectedBed, setSelectedBed] = useState(null);
  const [selectedSeat, setSelectedSeat] = useState(null);
  const [isGeneratingInvoices, setIsGeneratingInvoices] = useState(false);
  const [notesTarget, setNotesTarget] = useState(null);

  // Get token dynamically
  const getConfig = () => {
    const token = localStorage.getItem("token");
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    };
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.log("No token available, skipping fetch");
        return;
      }

      const endpoint =
        viewMode === "room"
          ? "hostel/beds/live-map/"
          : "library/seats/live-map/";
      const res = await axios.get(`${API}${endpoint}`, getConfig());
      setData(res.data);
    } catch (err) {
      toast.error("Failed to fetch data");
    }
  };

  const fetchRooms = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.log("No token available, skipping rooms fetch");
        return;
      }

      const res = await axios.get(`${API}hostel/rooms/`, getConfig());
      setRooms(res.data);
    } catch (err) {
      toast.error("Failed to fetch rooms");
    }
  };

  useEffect(() => {
    // Check if token is available before fetching
    const token = localStorage.getItem("token");
    if (token) {
      if (viewMode === "room") {
        fetchRooms();
      }
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  // Group beds by room for room view mode
  const groupBedsByRoom = (beds) => {
    const grouped = {};

    // Initialize all rooms with empty beds array
    rooms.forEach((room) => {
      grouped[room.id] = {
        roomId: room.id,
        roomNumber: room.room_number,
        roomCapacity: room.capacity,
        beds: [],
      };
    });

    // Add beds to their respective rooms
    beds.forEach((bed) => {
      const roomId = bed.room?.id;
      if (roomId && grouped[roomId]) {
        grouped[roomId].beds.push(bed);
      } else if (roomId) {
        // Handle case where bed has room_id but room not in rooms list
        grouped[roomId] = {
          roomId,
          roomNumber: bed.room?.room_number || `Room ${roomId}`,
          roomCapacity: bed.room?.capacity || 0,
          beds: [bed],
        };
      }
    });

    return Object.values(grouped);
  };

  const handleOpen = (item = null, room = null) => {
    if (item) {
      setEditItem(item);
      setForm({
        number: item.bed_number || item.seat_number,
        description: item.description || "",
        room_id: item.room?.id || "",
      });
    } else {
      setEditItem(null);
      setForm({
        number: "",
        description: "",
        room_id: room ? room.roomId : "",
      });
    }
    setOpenModal(true);
  };

  const handleClose = () => {
    setOpenModal(false);
    setEditItem(null);
    setForm({ number: "", description: "", room_id: "" });
  };

  const handleRoomModalOpen = () => {
    setOpenRoomModal(true);
    setEditRoom(null);
    setRoomForm({ room_number: "", capacity: "" });
  };

  const handleRoomModalClose = () => {
    setOpenRoomModal(false);
    setEditRoom(null);
    setRoomForm({ room_number: "", capacity: "" });
  };

  const handleRoomEdit = (room) => {
    setEditRoom(room);
    setRoomForm({
      room_number: room.roomNumber,
      capacity: room.roomCapacity.toString(),
    });
    setOpenRoomModal(true);
  };

  const showConfirmModal = (title, message, onConfirm) => {
    setConfirmModal({
      open: true,
      title,
      message,
      onConfirm,
    });
  };

  const closeConfirmModal = () => {
    setConfirmModal({
      open: false,
      title: "",
      message: "",
      onConfirm: null,
    });
  };

  const handleConfirm = async () => {
    if (confirmModal.onConfirm) {
      await confirmModal.onConfirm();
    }
    closeConfirmModal();
  };

  const handleRoomDelete = async (roomId) => {
    showConfirmModal(
      "Delete Room",
      "Are you sure you want to delete this room? This action cannot be undone and will remove all associated beds.",
      async () => {
        try {
          await axios.delete(`${API}hostel/rooms/${roomId}/`, getConfig());
          toast.success("Room deleted successfully");
          // Refresh both rooms and data to update UI
          await Promise.all([fetchRooms(), fetchData()]);
        } catch (err) {
          const errorMessage =
            err.response?.data?.error || "Failed to delete room";
          toast.error(errorMessage);
        }
      }
    );
  };

  const handleRoomSubmit = async () => {
    try {
      if (editRoom) {
        // Update room (PATCH)
        const payload = {
          room_number: roomForm.room_number,
          capacity: parseInt(roomForm.capacity),
        };

        await axios.patch(
          `${API}hostel/rooms/${editRoom.roomId}/`,
          payload,
          getConfig()
        );
        toast.success("Room updated successfully");
      } else {
        // Create room (POST)
        const payload = {
          room_number: roomForm.room_number,
          capacity: parseInt(roomForm.capacity),
        };

        await axios.post(`${API}hostel/rooms/`, payload, getConfig());
        toast.success("Room created successfully");
      }
      // Refresh both rooms and data to update UI
      await Promise.all([fetchRooms(), fetchData()]);
      handleRoomModalClose();
    } catch (err) {
      const errorMessage =
        err.response?.data?.room_number?.[0] ||
        err.response?.data?.error ||
        "Failed to save room";
      toast.error(errorMessage);
    }
  };

  const handleSubmit = async () => {
    // Duplicate check
    if (viewMode === "room") {
      // Find the room for the current form
      const roomId = form.room_id || editItem?.room?.id || 1;
      const roomBeds = data.filter((bed) => bed.room?.id === roomId);
      const duplicate = roomBeds.find(
        (bed) =>
          bed.bed_number === form.number &&
          (!editItem || bed.id !== editItem.id)
      );
      if (duplicate) {
        toast.error("A bed with this number already exists in this room.");
        return;
      }
    } else {
      // For seats, check globally
      const duplicate = data.find(
        (seat) =>
          seat.seat_number === form.number &&
          (!editItem || seat.id !== editItem.id)
      );
      if (duplicate) {
        toast.error("A seat with this number already exists.");
        return;
      }
    }

    const payload = {
      [viewMode === "room" ? "bed_number" : "seat_number"]: form.number,
      description: form.description,
      ...(viewMode === "room"
        ? { room_id: form.room_id || editItem?.room?.id || 1 }
        : {}),
    };

    try {
      if (editItem) {
        const endpoint =
          viewMode === "room"
            ? `hostel/beds/${editItem.id}/`
            : `library/seats/${editItem.id}/`;
        await axios.put(`${API}${endpoint}`, payload, getConfig());
        toast.success("Updated successfully");
      } else {
        const endpoint =
          viewMode === "room" ? "hostel/beds/" : "library/seats/";
        await axios.post(`${API}${endpoint}`, payload, getConfig());
        toast.success("Created successfully");
      }
      // Refresh data to update UI
      await fetchData();
      handleClose();
    } catch (err) {
      toast.error("Failed to submit");
    }
  };

  const handleDelete = async (id) => {
    showConfirmModal(
      "Delete " + (viewMode === "room" ? "Bed" : "Seat"),
      `Are you sure you want to delete this ${
        viewMode === "room" ? "bed" : "seat"
      }? This action cannot be undone.`,
      async () => {
        try {
          const endpoint =
            viewMode === "room" ? `hostel/beds/${id}/` : `library/seats/${id}/`;
          await axios.delete(`${API}${endpoint}`, getConfig());
          toast.success("Deleted successfully");
          // Refresh data to update UI
          await fetchData();
        } catch (err) {
          toast.error(err.response?.data?.error || "Failed to delete");
        }
      }
    );
  };

  const handleGenerateInvoices = async () => {
    setIsGeneratingInvoices(true);
    try {
      const endpoint =
        viewMode === "room"
          ? "hostel/admin/generate-central-invoices/"
          : "library/admin/generate-central-invoices/";

      const response = await axios.post(`${API}${endpoint}`, {}, getConfig());

      if (response.data.message) {
        toast.success(response.data.message);
      } else {
        toast.success("Invoices generated successfully");
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "Failed to generate invoices";
      toast.error(errorMessage);
    } finally {
      setIsGeneratingInvoices(false);
    }
  };

  // Render beds grouped by room
  const renderBedsByRoom = () => {
    const groupedRooms = groupBedsByRoom(data);

    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
        {groupedRooms.map((room) => (
          <Card key={room.roomId} className="shadow-lg">
            <CardHeader
              avatar={<MeetingRoom color="primary" />}
              title={
                <div className="flex justify-between items-center">
                  <span className="text-sm md:text-base font-semibold">
                    Room {room.roomNumber}
                  </span>
                  <div className="flex gap-1">
                    <IconButton
                      onClick={() => handleRoomEdit(room)}
                      size="small"
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      onClick={() => handleRoomDelete(room.roomId)}
                      size="small"
                      className="text-red-600 hover:text-red-800"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </div>
                </div>
              }
              subheader={`Capacity: ${room.roomCapacity} beds`}
              className="bg-blue-50 p-3 md:p-4"
            />
            <CardContent className="p-3 md:p-4">
              {room.beds.length === 0 ? (
                <Box className="text-center py-4 md:py-8">
                  <Typography
                    variant="body2"
                    className="text-gray-500 mb-2 text-sm md:text-base"
                  >
                    No beds assigned to this room
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Add />}
                    onClick={() => handleOpen(null, room)}
                    className="text-blue-600 border-blue-600 hover:bg-blue-50"
                  >
                    Add Bed
                  </Button>
                </Box>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-3">
                  {room.beds.map((bed) => (
                    <div
                      key={bed.id}
                      className={`relative border border-gray-200 p-2 md:p-3 rounded-lg shadow-sm hover:shadow-md transition-shadow ${
                        bed.is_paid ? "bg-[#ffeaea]" : "bg-white"
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div
                          onClick={() =>
                            (bed.status === "pending" ||
                              bed.status === "booked") &&
                            setSelectedBed(bed)
                          }
                          className="flex items-center gap-1 cursor-pointer relative"
                        >
                          <span className="relative flex items-center">
                            <HotelIcon
                              fontSize="large"
                              style={{
                                color:
                                  bed.status === "available"
                                    ? "#4CAF50"
                                    : bed.status === "booked"
                                    ? "#F44336"
                                    : bed.status === "pending"
                                    ? "#FF9800"
                                    : "#9CA3AF",
                              }}
                            />
                            {bed.is_paid && (
                              <svg
                                width="70"
                                height="32"
                                viewBox="0 0 70 32"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className="absolute -top-4 -left-6 rotate-[-15deg] z-10"
                                style={{ pointerEvents: "none" }}
                              >
                                <rect
                                  x="0"
                                  y="0"
                                  width="70"
                                  height="32"
                                  rx="8"
                                  fill="#fff"
                                  stroke="#F44336"
                                  strokeWidth="3"
                                />
                                <text
                                  x="35"
                                  y="22"
                                  textAnchor="middle"
                                  fill="#F44336"
                                  fontSize="20"
                                  fontWeight="bold"
                                  fontFamily="monospace"
                                  letterSpacing="2"
                                  style={{ textTransform: "uppercase" }}
                                >
                                  PAID
                                </text>
                              </svg>
                            )}
                            <span className="font-semibold text-sm md:text-lg ml-1">
                              {bed.bed_number}
                            </span>
                            {bed.pending_count > 1 && (
                              <span className="absolute -top-2 -right-3 bg-[#FF9800] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold shadow-lg animate-bounce z-10">
                                {bed.pending_count}
                              </span>
                            )}
                          </span>
                        </div>
                        <div className="flex gap-1">
                          <IconButton
                            onClick={() => setNotesTarget(bed)}
                            size="small"
                            title={`Notes (${bed.note_count || 0})`}
                            className="text-amber-600 hover:text-amber-800"
                          >
                            <NoteAlt fontSize="small" />
                          </IconButton>
                          <IconButton
                            onClick={() => handleOpen(bed, room)}
                            size="small"
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDelete(bed.id)}
                            size="small"
                            className="text-red-600 hover:text-red-800"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </div>
                      </div>
                      {bed.latest_note_preview && (
                        <div className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1 mb-2 line-clamp-2">
                          Note: {bed.latest_note_preview}
                        </div>
                      )}
                      {bed.description && (
                        <div
                          variant="body2"
                          className="text-gray-600 text-xs md:text-sm mb-2 line-clamp-2"
                        >
                          {bed.description}
                        </div>
                      )}
                      {/* {bed.invoice_expired && bed.status === "booked" && (
                        <span className="absolute bottom-1 right-1 bg-red-600 text-white text-[12px] font-bold rounded px-1.5 py-0.5 z-30 shadow">
                          Inv Exp
                        </span>
                      )} */}
                    </div>
                  ))}
                  {room.beds.length < room.roomCapacity && (
                    <div className="border-2 border-dashed border-gray-300 p-2 md:p-3 rounded-lg bg-gray-50 flex items-center justify-center min-h-[60px] md:min-h-[80px]">
                      <Button
                        variant="text"
                        size="small"
                        startIcon={<Add />}
                        onClick={() => handleOpen(null, room)}
                        className="text-gray-600 hover:text-blue-600 text-xs md:text-sm"
                      >
                        Add Bed
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  // Render library seats (unchanged)
  const renderLibrarySeats = () => {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
        {data.map((item) => (
          <div
            key={item.id}
            className={`relative border p-3 md:p-4 rounded-md shadow-sm ${
              item.is_paid ? "bg-[#ffeaea]" : "bg-white"
            }`}
          >
            <div className="flex justify-between items-start mb-2">
              <div
                onClick={() =>
                  (item.status === "pending" || item.status === "booked") &&
                  setSelectedSeat(item)
                }
                className="flex items-center gap-1 cursor-pointer relative"
              >
                <span className="relative flex items-center">
                  <ChairIcon
                    fontSize="large"
                    style={{
                      color:
                        item.status === "available"
                          ? "#4CAF50"
                          : item.status === "booked"
                          ? "#F44336"
                          : item.status === "pending"
                          ? "#FF9800"
                          : "#9CA3AF",
                    }}
                  />
                  {item.is_paid && (
                    <svg
                      width="70"
                      height="32"
                      viewBox="0 0 70 32"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="absolute -top-4 -left-6 rotate-[-15deg] z-10"
                      style={{ pointerEvents: "none" }}
                    >
                      <rect
                        x="0"
                        y="0"
                        width="70"
                        height="32"
                        rx="8"
                        fill="#fff"
                        stroke="#F44336"
                        strokeWidth="3"
                      />
                      <text
                        x="35"
                        y="22"
                        textAnchor="middle"
                        fill="#F44336"
                        fontSize="20"
                        fontWeight="bold"
                        fontFamily="monospace"
                        letterSpacing="2"
                        style={{ textTransform: "uppercase" }}
                      >
                        PAID
                      </text>
                    </svg>
                  )}
                  <span className="font-semibold text-sm md:text-lg ml-2">
                    {item.seat_number}
                  </span>
                  {item.pending_count > 1 && (
                    <span className="absolute -top-2 -right-3 bg-[#FF9800] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold shadow-lg animate-bounce z-10">
                      {item.pending_count}
                    </span>
                  )}
                </span>
              </div>
              <div className="flex gap-1">
                <IconButton onClick={() => setNotesTarget(item)} size="small" title={`Notes (${item.note_count || 0})`}>
                  <NoteAlt fontSize="small" />
                </IconButton>
                <IconButton onClick={() => handleOpen(item)} size="small">
                  <Edit fontSize="small" />
                </IconButton>
                <IconButton onClick={() => handleDelete(item.id)} size="small">
                  <Delete fontSize="small" />
                </IconButton>
              </div>
            </div>
            {item.latest_note_preview && (
              <p className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1 mb-2 line-clamp-2">Note: {item.latest_note_preview}</p>
            )}
            <p className="text-xs md:text-sm text-gray-600 mb-2 line-clamp-2">
              {item.description}
            </p>
            {/* {item.invoice_expired && item.status === "booked" && (
              <span className="absolute bottom-1 right-1 bg-red-600 text-white text-[12px] font-bold rounded px-1.5 py-0.5 z-30 shadow">
                Inv Exp
              </span>
            )} */}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="p-3 md:p-4">
      {/* Status Legend */}
      <div className="flex justify-between flex-col sm:flex-row gap-4 md:gap-0 items-center text-sm font-medium text-gray-700 mb-4">
        <div className="flex gap-6">
          <div className="flex items-center gap-2">
            <div
              className="w-4 h-4 rounded"
              style={{ backgroundColor: "#4CAF50" }}
            ></div>
            Available
          </div>
          <div className="flex items-center gap-2">
            <div
              className="w-4 h-4 rounded"
              style={{ backgroundColor: "#FF9800" }}
            ></div>
            Pending
          </div>
          <div className="flex items-center gap-2">
            <div
              className="w-4 h-4 rounded"
              style={{ backgroundColor: "#F44336" }}
            ></div>
            Booked
          </div>
        </div>
        <Button
          startIcon={<Receipt />}
          variant="contained"
          onClick={handleGenerateInvoices}
          disabled={isGeneratingInvoices}
          className="bg-green-600 hover:bg-green-700 text-white text-sm"
          size="small"
        >
          {isGeneratingInvoices
            ? "Generating..."
            : `Generate ${viewMode === "room" ? "Hostel" : "Library"} Invoices`}
        </Button>
      </div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4 md:mb-6">
        {/* <h2 className="text-xl font-bold capitalize">
          {viewMode === "room" ? "Beds" : "Library Seats"} Manager
        </h2> */}
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          {viewMode === "room" && (
            <Button
              startIcon={<Add />}
              variant="outlined"
              onClick={handleRoomModalOpen}
              className="border-blue-600 text-blue-600 hover:bg-blue-50 text-sm md:text-base"
              size="small"
            >
              Add Room
            </Button>
          )}
          {viewMode !== "room" && (
            <Button
              startIcon={<Add />}
              variant="outlined"
              onClick={() => handleOpen()}
              className="border-blue-600 text-blue-600 hover:bg-blue-50 text-sm md:text-base"
              size="small"
            >
              Add {viewMode === "room" ? "Bed" : "Seat"}
            </Button>
          )}
        </div>
      </div>

      {data.length === 0 && rooms.length === 0 ? (
        <Box className="text-center py-6 md:py-8">
          <Typography
            variant="h6"
            className="text-gray-500 text-base md:text-xl"
          >
            No {viewMode === "room" ? "rooms" : "seats"} available.
          </Typography>
        </Box>
      ) : viewMode === "room" ? (
        renderBedsByRoom()
      ) : (
        renderLibrarySeats()
      )}

      <BedSeatNotesModal
        open={Boolean(notesTarget)}
        onClose={() => setNotesTarget(null)}
        item={notesTarget}
        type={viewMode === "room" ? "bed" : "seat"}
        onChanged={fetchData}
      />

      <Dialog open={openModal} onClose={handleClose} fullWidth>
        <DialogTitle>
          {editItem ? "Update" : "Create"}{" "}
          {viewMode === "room" ? "Bed" : "Seat"}
          {viewMode === "room" && !editItem && form.room_id && (
            <Typography variant="subtitle2" className="text-gray-600 mt-1">
              for Room {rooms.find((r) => r.id === form.room_id)?.room_number}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            paddingTop: "6px !important",
          }}
        >
          <TextField
            label={viewMode === "room" ? "Bed Number/Name" : "Seat Number/Name"}
            value={form.number}
            onChange={(e) => setForm({ ...form, number: e.target.value })}
            required
            disabled={editItem && editItem.status !== "available"}
            helperText={
              editItem && editItem.status !== "available"
                ? `${
                    viewMode === "room" ? "Bed" : "Seat"
                  } number can only be changed when status is Available`
                : ""
            }
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editItem ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Room Creation Modal */}
      <Dialog
        open={openRoomModal}
        onClose={handleRoomModalClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{editRoom ? "Edit Room" : "Create New Room"}</DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            paddingTop: "6px !important",
          }}
        >
          {/* {!editRoom && ( */}
          <TextField
            label="Room Number/Name"
            type="text"
            value={roomForm.room_number}
            onChange={(e) =>
              setRoomForm({ ...roomForm, room_number: e.target.value })
            }
            required
            fullWidth
          />
          {/* )} */}
          <TextField
            label="Capacity"
            type="number"
            value={roomForm.capacity}
            onChange={(e) =>
              setRoomForm({ ...roomForm, capacity: e.target.value })
            }
            required
            fullWidth
            inputProps={{ min: 1, max: 10 }}
            helperText="Number of beds this room can accommodate"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRoomModalClose}>Cancel</Button>
          <Button
            onClick={handleRoomSubmit}
            variant="contained"
            disabled={!roomForm.room_number || !roomForm.capacity}
          >
            {editRoom ? "Update Room" : "Create Room"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Modal */}
      <Dialog
        open={confirmModal.open}
        onClose={closeConfirmModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          className: "rounded-lg",
        }}
      >
        <DialogTitle className="flex items-center gap-2 text-red-600">
          <Delete color="error" />
          {confirmModal.title}
        </DialogTitle>
        <DialogContent
          sx={{
            paddingTop: "6px !important",
            paddingBottom: "16px !important",
          }}
        >
          <Typography variant="body1" className="text-gray-700">
            {confirmModal.message}
          </Typography>
        </DialogContent>
        <DialogActions className="p-4 gap-2">
          <Button
            onClick={closeConfirmModal}
            variant="outlined"
            className="border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="error"
            className="bg-red-600 hover:bg-red-700"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modals for bed actions */}
      {selectedBed && selectedBed.status === "pending" && (
        <PendingBookingsModal
          bedSeatId={selectedBed.id}
          type="hostel"
          onClose={() => setSelectedBed(null)}
        />
      )}
      {selectedSeat && selectedSeat.status === "pending" && (
        <PendingBookingsModal
          bedSeatId={selectedSeat.id}
          type="library"
          onClose={() => setSelectedSeat(null)}
        />
      )}
      {selectedBed && selectedBed.status === "booked" && (
        <BookingModal
          bookingId={selectedBed.booking_id}
          type="hostel"
          onClose={() => setSelectedBed(null)}
          onAction={() => {
            fetchData();
            fetchRooms();
          }}
        />
      )}
      {selectedSeat && selectedSeat.status === "booked" && (
        <BookingModal
          bookingId={selectedSeat.booking_id}
          type="library"
          onClose={() => setSelectedSeat(null)}
          onAction={() => {
            fetchData();
            fetchRooms();
          }}
        />
      )}
    </div>
  );
};

export default RoomLibraryManager;
